import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import { AppShell } from "@/components/AppShell";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loading } from "@/components/kit";
import { ToastProvider } from "@/components/toast";
import { AuthProvider, useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import { UploadProvider } from "@/lib/uploads";
import AuthPage from "@/pages/AuthPage";
import BrandingPage from "@/pages/BrandingPage";
import CreateVideoPage from "@/pages/CreateVideoPage";
import ProjectPage from "@/pages/ProjectPage";
import ProjectsPage from "@/pages/ProjectsPage";
import RenderPage from "@/pages/RenderPage";

function Private() {
  const { status } = useAuth();
  if (status === "loading") return <Loading label="Signing you in…" className="min-h-screen" />;
  return status === "in" ? <Outlet /> : <Navigate to="/sign-in" replace />;
}

function Public() {
  const { status } = useAuth();
  if (status === "loading") return <Loading label="Signing you in…" className="min-h-screen" />;
  return status === "in" ? <Navigate to="/projects" replace /> : <Outlet />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <UploadProvider>
              <BrowserRouter>
                <Routes>
                  <Route element={<Public />}>
                    <Route path="/sign-in" element={<AuthPage mode="sign-in" />} />
                    <Route path="/sign-up" element={<AuthPage mode="sign-up" />} />
                  </Route>
                  <Route element={<Private />}>
                    <Route element={<AppShell />}>
                      <Route path="/projects" element={<ProjectsPage />} />
                      <Route path="/projects/new" element={<ProjectsPage creating />} />
                      <Route path="/projects/:id" element={<ProjectPage />} />
                      <Route path="/projects/:id/create" element={<CreateVideoPage />} />
                      <Route path="/renders/:id" element={<RenderPage />} />
                      <Route path="/branding" element={<BrandingPage />} />
                    </Route>
                  </Route>
                  <Route path="*" element={<Navigate to="/projects" replace />} />
                </Routes>
              </BrowserRouter>
            </UploadProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
